import React, { useEffect, useMemo, useState } from 'react';
import { groupBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import ModalLayout from '../../../layouts/Modal/ModalLayout';
import StaticNavbar from '../../../layouts/StaticNavbarLayoutMC';
import TitleMC from '../../../components/TitleMC';
import ButtonIconMC from '../../../components/ButtonIconMC';
import ButtonComponent from '../../../components/ButtonMC';
import IconMC from '../../../components/IconMC';
import WhatsappPhoneMockupMC from './WhatsappPhoneMockupMC';
import parseFlowJsonPreview from '../utils/parseFlowJsonPreview';
import openIntercom from '../../../utils/openIntercom';
import WhatsappFlowTemplatesModel from '../../../models/WhatsappFlowTemplatesModelMC';
import {
  META_BUSINESS_URL,
  getWhatsappFlowTemplateRoute
} from '../../../../constants/WhatsappFlowTemplateCategories';

const normalizePreviewConfig = (previewConfig = {}) => ({
  contactName: previewConfig.contactName || previewConfig.contact_name || '',
  companyMessage: previewConfig.companyMessage || previewConfig.company_message || '',
  ctaLabel: previewConfig.ctaLabel || previewConfig.cta_label || '',
  formTitle: previewConfig.formTitle || previewConfig.form_title || '',
  submitLabel: previewConfig.submitLabel || previewConfig.submit_label || '',
  fields: previewConfig.fields || []
});

const JSON_STEP_KEYS = ['step1', 'step2', 'step3', 'step4'];

const WhatsappFlowTemplateModal = ({
  visible,
  onClose,
  onCloseCreationModal,
  onSync
}) => {
  const [routes, setRoutes] = useState([]);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const { t } = useTranslation('WhatsappFlowsScreenMC');

  useEffect(() => {
    if (!visible) return;

    WhatsappFlowTemplatesModel.get()
      .then((templates) => {
        const grouped = groupBy(templates, 'category');
        const newRoutes = Object.entries(grouped).map(
          ([category, categoryTemplates]) => getWhatsappFlowTemplateRoute(
            category,
            categoryTemplates,
            setCurrentTemplate
          )
        );

        setRoutes(newRoutes);
        newRoutes[0]?.subRoutes[0]?.routeProps?.onClick?.();
      })
      .catch((error) => {
        console.error('Failed to load WhatsApp flow templates:', error);
      });
  }, [visible]);

  const previewConfig = useMemo(
    () => normalizePreviewConfig(currentTemplate?.preview_config),
    [currentTemplate]
  );

  const previewScreens = useMemo(
    () => parseFlowJsonPreview(currentTemplate?.flow_json, previewConfig.fields),
    [currentTemplate, previewConfig.fields]
  );

  const screenLabels = useMemo(
    () => ({
      indicator: (current, total) => t('templates_modal.screen_indicator', { current, total })
    }),
    [t]
  );

  const flowJsonString = useMemo(() => {
    if (!currentTemplate?.flow_json) return '';
    return JSON.stringify(currentTemplate.flow_json, null, 2);
  }, [currentTemplate]);

  const handleCopyJson = async () => {
    if (!flowJsonString) return;

    try {
      await navigator.clipboard.writeText(flowJsonString);
      if (window.showtoast) window.showtoast(t('templates_modal.copy_json_success'));
    } catch (error) {
      console.error('Failed to copy flow JSON:', error);
      if (window.showtoast) window.showtoast(t('templates_modal.copy_json_error'));
    }
  };

  const handleOpenMeta = () => {
    window.open(META_BUSINESS_URL, '_blank', 'noopener,noreferrer');
  };

  const handleSync = () => {
    onClose();
    onCloseCreationModal?.();
    onSync?.();
  };

  const handleOpenSupport = () => {
    openIntercom();
  };

  return (
    <ModalLayout
      visible={visible}
      onClose={onClose}
      className="tw-h-screen tw-rounded-none !tw-max-h-none !tw-overflow-hidden !tw-p-0"
      containerClassName="!tw-max-w-none tw-ml-[60px] tw-overflow-hidden"
    >
      <div className="tw-flex tw-h-full tw-flex-col tw-overflow-hidden">
        <div className="tw-shrink-0 tw-border-b tw-border-gray-200">
          <div className="tw-mx-4 tw-flex tw-h-14 tw-items-center tw-justify-between">
            <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center">
              <ButtonIconMC
                icon="material-symbols-arrow-back-rounded"
                onClick={onClose}
                iconClassName="!tw-h-5 !tw-w-5 tw-mt-1"
              />
              <TitleMC text={t('templates_modal.header')} textClassName="tw-text-base !tw-font-semibold tw-mb-0" />
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('templates_modal.close')}
              className="tw-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-text-gray-500 hover:tw-bg-gray-100 hover:tw-text-gray-700"
            >
              <IconMC name="close-outline" size="lg" />
            </button>
          </div>
        </div>

        <div className="tw-min-h-0 tw-flex-1">
          <StaticNavbar
            fillParent
            routes={routes}
            currentRoute={currentTemplate?.web_id}
            navbarClassName="lg:!tw-ml-0 !tw-ml-0 !tw-pt-3"
            title={t('templates_modal.navbar_title')}
            className="!tw-bg-gray-200"
          >
            <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col lg:tw-flex-row">
              <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-bg-gray-50 tw-px-4 tw-py-2 lg:tw-min-w-[420px]">
                {currentTemplate?.name && (
                  <div className="tw-mb-2 tw-shrink-0 tw-text-center">
                    <h3 className="tw-line-clamp-1 tw-text-sm tw-font-semibold tw-text-gray-900">
                      {currentTemplate.name}
                    </h3>
                    <p className="tw-line-clamp-1 tw-text-xs tw-text-gray-600">
                      {currentTemplate.description}
                    </p>
                  </div>
                )}

                <div className="tw-min-h-0 tw-flex-1">
                  <WhatsappPhoneMockupMC
                    key={currentTemplate?.web_id}
                    size="large"
                    fillContainer
                    previewKey={currentTemplate?.web_id}
                    contactName={previewConfig.contactName}
                    companyMessage={previewConfig.companyMessage}
                    ctaLabel={previewConfig.ctaLabel}
                    formTitle={previewConfig.formTitle}
                    submitLabel={previewConfig.submitLabel}
                    fields={previewConfig.fields}
                    screens={previewScreens}
                    screenLabels={screenLabels}
                    onlineLabel={t('landing.mockup.online')}
                  />
                </div>
              </div>

              <div className="tw-flex tw-min-h-0 tw-w-full tw-flex-col tw-border-t tw-border-gray-200 tw-bg-white lg:tw-w-[480px] lg:tw-shrink-0 lg:tw-border-l lg:tw-border-t-0">
                <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-p-4">
                  <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-gap-2">
                    <p className="tw-text-sm tw-font-semibold tw-text-gray-800">{t('templates_modal.json_title')}</p>
                    <ButtonComponent
                      text={t('templates_modal.copy_json')}
                      onClick={handleCopyJson}
                      size="small"
                      icon="copy-outline"
                      iconPosition="left"
                      className="!tw-text-xs"
                    />
                  </div>
                  <pre className="tw-mt-2 tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden tw-whitespace-pre-wrap tw-break-words tw-rounded-xl tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-4 tw-text-xs tw-text-gray-800">
                    {flowJsonString}
                  </pre>
                  <div className="tw-mt-3 tw-shrink-0 tw-rounded-xl tw-border tw-border-blue-100 tw-bg-blue-50 tw-p-3">
                    <p className="tw-text-xs tw-font-semibold tw-text-gray-800">
                      {t('templates_modal.json_steps.title')}
                    </p>
                    <ol className="tw-mt-2 tw-list-decimal tw-space-y-1.5 tw-pl-4 tw-text-xs tw-leading-5 tw-text-gray-600">
                      {JSON_STEP_KEYS.map((stepKey) => (
                        <li key={stepKey}>{t(`templates_modal.json_steps.${stepKey}`)}</li>
                      ))}
                    </ol>
                  </div>

                  <div className="tw-mt-3 tw-flex tw-shrink-0 tw-flex-col tw-gap-2">
                    <ButtonComponent
                      text={t('templates_modal.open_meta')}
                      onClick={handleOpenMeta}
                      variant="SECONDARY"
                      outlined
                      icon="external-link-outline"
                      iconPosition="right"
                      className="!tw-w-full !tw-text-xs"
                    />
                    <ButtonComponent
                      text={t('templates_modal.sync_after_create')}
                      onClick={handleSync}
                      icon="sync-outline"
                      iconPosition="left"
                      className="!tw-w-full !tw-text-xs"
                    />
                  </div>
                </div>

                <div className="tw-shrink-0 tw-border-t tw-border-gray-200 tw-bg-gray-50 tw-px-4 tw-py-3">
                  <div className="tw-flex tw-items-start tw-gap-2">
                    <IconMC name="mingcute-question-line" className="tw-mt-0.5 tw-flex-shrink-0 tw-text-blue-500" />
                    <div className="tw-text-xs tw-leading-5 tw-text-gray-500">
                      <p>
                        {t('templates_modal.help.prefix')}{' '}
                        <button
                          type="button"
                          onClick={handleOpenSupport}
                          className="tw-font-medium tw-text-blue-600 hover:tw-underline"
                        >
                          {t('templates_modal.help.support_link')}
                        </button>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </StaticNavbar>
        </div>
      </div>
    </ModalLayout>
  );
};

export default WhatsappFlowTemplateModal;
